import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";

import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import QuoteSelectionView from "../../Components/QuoteSelectionView";
import { Message } from "primereact/message";
import { QUOTE_STATUS_F2, desfechoPorTrechoDe } from "../../Constants/quote";
import { getPublicQuote, approvePublicQuote, reopenPublicSelection, requestReservationVoucher } from "../../Services/quotePublic";
// O botão do voucher mora em Components/ desde 2026-08-07: o painel da agência também
// dispara o envio, por uma rota autenticada própria. Cada tela passa a sua chamada.
import VoucherButton from "../../Components/VoucherButton";
import { normalizePublicQuote } from "../../Store/Actions/Quote";
import { formatDateBR } from "../../Components/Utils";
import { descreverCambio } from "../../Utils/cambioJanela";
import dream4Logo from "../../Assets/Images/logoHeader.svg";

/**
 * Página pública de SELEÇÃO + APROVAÇÃO da cotação (Fase 2).
 * Rota /cotacao/:token — fora do layout B2B, sem auth (autorizada pelo token). No open o
 * BE re-cota ao vivo (disponibilidade/preços/câmbio) e registra a visualização. Cliente
 * pode refazer a seleção após aprovar. Reflete o status de pagamento das reservas.
 */
const QuoteSelection = () => {
	const { token } = useParams();
	const [quote, setQuote] = useState(undefined); // undefined=carregando, null=não encontrada
	const [recotando, setRecotando] = useState(true);
	const [aprovando, setAprovando] = useState(false);
	const [aprovado, setAprovado] = useState(false);
	const [refazendo, setRefazendo] = useState(false);
	const [erroAprovar, setErroAprovar] = useState("");

	// Carrega/recarrega a cotação pública. O GET re-cota ao vivo e registra a visualização no BE.
	const carregar = useCallback(async ({ withSpinner = false } = {}) => {
		if (withSpinner) setRecotando(true);
		try {
			const q = normalizePublicQuote(await getPublicQuote(token));
			setQuote(q || null);
			// A janela do câmbio vem PRONTA do backend (`atualizadoEm`/`validoAte`/`congelado`,
			// entregues em 2026-09-04) e a frase é montada por Utils/cambioJanela.
			//
			// Aqui havia uma formatação à mão e um comentário afirmando que "a taxa é a de hoje e
			// muda amanhã". Era falso nos dois lados: a taxa da SMI é publicada uma vez por dia e a
			// validade ATRAVESSA a virada — o próprio BE mediu 00:05 de 04/09 exibindo a taxa de
			// 03/09, que expirava às 10:59 daquela manhã.
			//
			// NÃO calcular a janela aqui: o horário de publicação é regra do fornecedor, e derivá-la
			// dos dois lados é a forma exata do BUG-004.
		} catch (e) {
			setQuote(null);
		} finally {
			setRecotando(false);
		}
	}, [token]);

	useEffect(() => { carregar({ withSpinner: true }); }, [carregar]);

	// Item 11 — confirmação explicando que a agência fará a reserva a partir da
	// cotação e que pode haver pequenas variações de valores (pra cima ou pra baixo).
	// Seleção PARCIAL (blocos sem escolha) troca a mensagem pelo aviso de descarte.
	const aprovar = (selecao, info) => confirmDialog({
		message: info?.parcial
			? `Você deixou ${info.naoSelecionados.length} bloco(s) sem seleção${info.naoSelecionados.length ? ` (${info.naoSelecionados.join(", ")})` : ""}. `
				+ "O que não foi selecionado será descartado e não entrará na reserva — se ainda quiser esses trechos, peça uma nova cotação à agência. "
				+ "Os valores podem sofrer pequenas variações (para cima ou para baixo) até a confirmação da reserva. Deseja aprovar assim?"
			: "Ao aprovar, sua seleção é enviada à agência, que fará a reserva a partir desta cotação. Os valores podem sofrer pequenas variações (para cima ou para baixo) até a confirmação da reserva. Deseja aprovar?",
		header: info?.parcial ? "Aprovar apenas parte da cotação" : "Aprovar cotação",
		icon: info?.parcial ? "pi pi-exclamation-triangle" : "pi pi-check-circle",
		style: { width: "min(480px, 92vw)" },
		acceptLabel: "Sim, aprovar", rejectLabel: "Cancelar",
		accept: () => {
			setAprovando(true); setErroAprovar("");
			approvePublicQuote(token, selecao)
				.then(() => { setAprovando(false); setAprovado(true); carregar(); })
				.catch((e) => {
					setAprovando(false);
					const code = e?.response?.data?.error || e?.response?.data?.code;
					// 409 / item indisponível: o BE atualiza as opções → recarrega e pede nova seleção.
					if (e?.response?.status === 409 || code === "ITEM_UNAVAILABLE") {
						setErroAprovar("Alguns itens ficaram indisponíveis. Atualizamos as opções — revise sua seleção e aprove novamente.");
						carregar({ withSpinner: true });
					} else if (code === "EMPTY_SELECAO") {
						setErroAprovar("Selecione ao menos uma opção para aprovar.");
					} else {
						setErroAprovar(e?.response?.data?.errorMessage || "Não foi possível aprovar agora. Tente novamente.");
					}
				});
		},
	});

	const refazer = () => confirmDialog({
		message: "Sua seleção atual será reaberta para ajuste e a agência será avisada. Deseja continuar?",
		header: "Ajustar minha seleção", icon: "pi pi-pencil", style: { width: "min(440px, 92vw)" },
		acceptLabel: "Sim, ajustar", rejectLabel: "Cancelar",
		accept: () => {
			setRefazendo(true);
			reopenPublicSelection(token)
				.then(() => { setAprovado(false); return carregar(); })
				.catch(() => {})
				.finally(() => setRefazendo(false));
		},
	});

	if (quote === undefined) return <Shell><Loading /></Shell>;
	if (quote === null) return <Shell><Estado titulo="Cotação não encontrada" texto="Verifique o link recebido ou solicite um novo à sua agência." /></Shell>;
	if (recotando) return <Shell nome={quote.nome}><Loading /></Shell>;

	if (quote.status === QUOTE_STATUS_F2.RASCUNHO) {
		return <Shell nome={quote.nome}><Estado titulo="Cotação indisponível no momento"
			texto="Sua agência está atualizando esta cotação. Você será notificado quando ela estiver pronta para seleção." /></Shell>;
	}

	if (aprovado || quote.status === QUOTE_STATUS_F2.APROVADA) {
		return <Shell nome={quote.nome}>
			<Estado icone="pi-check-circle" cor="#2E9E5B" titulo="Cotação aprovada! 🎉"
				texto="Recebemos sua seleção. A agência já foi notificada e vai preparar suas reservas." />
			{/* Aprovada ≠ reservada: até a agência efetivar, nada está garantido. */}
			<Message severity="warn" style={{ width: "100%", margin: "0.5rem 0" }}
				text="Atenção: a reserva ainda não foi efetivada. Até que ela seja concluída, a tarifa e a disponibilidade das opções podem mudar, e o valor em reais acompanha a variação do câmbio. A agência confirma com você caso algo mude." />
			<div style={{ textAlign: "center", margin: "1rem 0" }}>
				<Button label="Errei algo? Ajustar minha seleção" icon="pi pi-pencil" outlined onClick={refazer} loading={refazendo} disabled={refazendo} />
			</div>
			<h3 style={{ color: "#2E0249", margin: "1.5rem 0 0.75rem" }}>O que você aprovou</h3>
			<QuoteSelectionView quote={quote} selecao={quote.selecaoCliente} readOnly />
		</Shell>;
	}

	if ([QUOTE_STATUS_F2.RESERVADA, QUOTE_STATUS_F2.GANHA, QUOTE_STATUS_F2.PARCIALMENTE_GANHA].includes(quote.status)) {
		return <Shell nome={quote.nome}>
			<ReservasStatus quote={quote} token={token} />
			<h3 style={{ color: "#2E0249", margin: "1.5rem 0 0.75rem" }}>Sua seleção</h3>
			{/* Com mais de uma reserva, cada bloco carrega o próprio desfecho — sem isso a
			    lista de cima dizia "1 paga, 1 cancelada" e a seleção aqui embaixo não
			    deixava claro a qual hotel cada uma correspondia (owner 2026-08-06). */}
			<QuoteSelectionView quote={quote} selecao={quote.selecaoCliente} readOnly
				desfechoPorTrecho={desfechoPorTrechoDe(quote)} />
		</Shell>;
	}

	// Encerrada: o BE (2026-08-03) passou a devolver o SNAPSHOT em vez de 404. Mostramos
	// o conteúdo em leitura — a seleção aprovada, se houve; senão as opções apresentadas.
	// O título depende do MOTIVO: reserva cancelada é "cancelada" (o cliente chegou a ter
	// reserva); sem reserva é uma cotação que simplesmente não avançou.
	if (quote.status === QUOTE_STATUS_F2.PERDIDA) {
		const temSelecao = quote.selecaoCliente && Object.keys(quote.selecaoCliente).length > 0;
		const reservas = quote.reservas || [];
		const houveCancelamento = reservas.some((r) => r.status === "cancelada");
		return <Shell nome={quote.nome}>
			<Estado icone={houveCancelamento ? "pi-times-circle" : undefined} cor={houveCancelamento ? "#C0392B" : undefined}
				titulo={houveCancelamento ? "Reserva cancelada" : "Cotação encerrada"}
				texto={houveCancelamento
					? "As reservas desta cotação foram canceladas. Nada será cobrado. Fale com sua agência se quiser retomar a viagem."
					: "Esta cotação não está mais ativa e não pode receber alterações. Fale com sua agência se precisar retomá-la."} />
			{reservas.length ? <ReservasCanceladas reservas={reservas} /> : null}
			<h3 style={{ color: "#2E0249", margin: "1.5rem 0 0.75rem" }}>
				{temSelecao ? "O que havia sido selecionado" : "As opções que foram apresentadas"}
			</h3>
			{/* Encerrada não é vitrine: sem "aprovar", sem re-cota, e os valores são só
			    referência do que foi cotado. */}
			<QuoteSelectionView quote={quote} historico
				selecao={temSelecao ? quote.selecaoCliente : undefined}
				readOnly={temSelecao} preview={!temSelecao} hideBanner
				desfechoPorTrecho={desfechoPorTrechoDe(quote)} />
			<div style={{ color: "#9aa0a6", fontSize: "0.78rem", marginTop: "0.75rem" }}>
				Os valores acima são apenas o registro do que foi cotado e não valem como oferta.
			</div>
		</Shell>;
	}

	// aguardando_selecao → seleção
	return (
		<Shell nome={quote.nome} contato={quote.contato} cambio={quote.cambio}>
			{erroAprovar ? <Message severity="warn" text={erroAprovar} style={{ width: "100%", marginBottom: "1rem" }} /> : null}
			<QuoteSelectionView quote={quote} onAprovar={aprovar} aprovando={aprovando} actorLabel="cliente" />
		</Shell>
	);
};

// Reservas de uma cotação encerrada — o cliente precisa enxergar o que foi cancelado.
const ReservasCanceladas = ({ reservas }) => (
	<div style={{ marginTop: "0.5rem" }}>
		{reservas.map((r) => {
			const cancelada = r.status === "cancelada";
			return (
				<div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "10px 0", borderTop: "1px solid #EEE", flexWrap: "wrap" }}>
					<div>
						<div style={{ fontWeight: 600 }}>{r.hotel || (r.codigoReserva ? `Reserva ${r.codigoReserva}` : "Reserva")}</div>
						<div style={{ color: "#6B6B6B", fontSize: "0.85rem" }}>
							{fmtDia(r.periodo?.checkIn)} → {fmtDia(r.periodo?.checkOut)}
							{r.codigoReserva ? <> · localizador <strong>{r.codigoReserva}</strong></> : null}
						</div>
						{/* motivoCancelamento ainda não vem do BE — renderiza sozinho quando vier. */}
						{cancelada && r.motivoCancelamento ? (
							<div style={{ color: "#C0392B", fontSize: "0.82rem" }}>Motivo: {r.motivoCancelamento}</div>
						) : null}
					</div>
					<Tag value={cancelada ? "Cancelada" : r.status === "paga" ? "Pago ✓" : "Não efetivada"}
						severity={cancelada ? "danger" : r.status === "paga" ? "success" : "warning"} />
				</div>
			);
		})}
	</div>
);

// YYYY-MM-DD -> DD/MM/YYYY (datas das reservas na página pública).
// BUG-004: `prazoPagamento` passou a chegar como INSTANTE (espelha o Booking.cancellationDeadline).
// O corte em 10 caracteres mostrava o dia em UTC — "pagar até 17/09" num prazo que vence 16/09 às
// 23:59. Esta é a página que o CLIENTE FINAL lê: a data aqui é compromisso.
const fmtDia = (d) => formatDateBR(d);

const FORMA_PAGAMENTO = { cartao: "Cartão de crédito", pix: "PIX", outro: "Outra forma de pagamento" };

// Reflete pagamento das reservas na página pública.
const ReservasStatus = ({ quote, token }) => {
	const overall = quote.status === QUOTE_STATUS_F2.GANHA
		? { sev: "success", ic: "pi-check-circle", cor: "#2E9E5B", tit: "Pagamento concluído! 🎉", txt: "Todas as suas reservas estão confirmadas. Bons momentos!" }
		: quote.status === QUOTE_STATUS_F2.PARCIALMENTE_GANHA
			? { sev: "warning", ic: "pi-info-circle", cor: "#B26A00", tit: "Parte das reservas foi confirmada", txt: "Parte do pagamento foi concluído. Fale com a agência sobre os itens pendentes/cancelados." }
			: { sev: "info", ic: "pi-clock", cor: "#814DE5", tit: "Reservas realizadas — aguardando pagamento", txt: "Suas reservas foram feitas. Efetue o pagamento pelo link enviado por e-mail dentro do prazo." };
	const statusReserva = (s) => s === "paga" ? { label: "Pago ✓", severity: "success" }
		: s === "cancelada" ? { label: "Cancelada", severity: "danger" }
		: { label: "Aguardando pagamento", severity: "warning" };
	return (
		<div>
			<div style={{ textAlign: "center", marginBottom: "1rem" }}>
				<i className={`pi ${overall.ic}`} style={{ fontSize: "2rem", color: overall.cor }} />
				<h2 style={{ color: "#2E0249", margin: "0.5rem 0 0.25rem" }}>{overall.tit}</h2>
				<p style={{ color: "#6B6B6B", margin: 0 }}>{overall.txt}</p>
			</div>
			{/* Shape público (BE 2026-08-04): hotel, cidade/país, datas, localizador e status.
			    Sem valores — a página abre por link, sem autenticação. */}
			{(quote.reservas || []).map((r) => {
				const st = statusReserva(r.status);
				const local = [r.cidade, r.pais].filter(Boolean).join(", ");
				return (
					<div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "10px 0", borderTop: "1px solid #EEE", flexWrap: "wrap" }}>
						<div>
							{local ? <div style={{ color: "#814DE5", fontWeight: "bold", fontSize: "0.85rem" }}>
								<i className="pi pi-map-marker" style={{ fontSize: "0.75rem", marginRight: 3 }} />{local}
							</div> : null}
							{/* hotelNome vem do item do trecho no BE e pode ser null se o trecho
							    não tiver item mapeado — não deixa a linha sem título. */}
							<div style={{ fontWeight: 600 }}>{r.hotel || (r.codigoReserva ? `Reserva ${r.codigoReserva}` : "Reserva")}</div>
							<div style={{ color: "#6B6B6B", fontSize: "0.85rem" }}>
								{fmtDia(r.periodo?.checkIn)} → {fmtDia(r.periodo?.checkOut)}
								{r.codigoReserva ? <> · localizador <strong>{r.codigoReserva}</strong></> : null}
							</div>
							{/* Prazo só faz sentido em pendente: o BE mantém o campo preenchido
							    em paga/cancelada, onde exibi-lo confundiria o cliente. */}
							{r.status === "pendente" && r.prazoPagamento ? (
								<div style={{ color: "#B26A00", fontSize: "0.85rem", fontWeight: 600 }}>
									<i className="pi pi-clock" style={{ fontSize: "0.75rem", marginRight: 4 }} />
									Pagar até {fmtDia(r.prazoPagamento)}
								</div>
							) : null}
							{/* Reserva paga: forma de pagamento e parcelamento. Sem valor da parcela
							    — decisão do owner, a página abre por link sem autenticação. */}
							{r.status === "paga" && (r.formaPagamento || r.parcelas) ? (
								<div style={{ color: "#6B6B6B", fontSize: "0.82rem" }}>
									{FORMA_PAGAMENTO[r.formaPagamento] || r.formaPagamento || ""}
									{r.parcelas > 1 ? ` · ${r.parcelas}x` : r.parcelas === 1 ? " · à vista" : ""}
								</div>
							) : null}
							{/* Motivo do cancelamento: rótulo do catálogo (o texto livre do operador
							    NUNCA vem para o payload público). */}
							{r.status === "cancelada" && r.motivoCancelamento ? (
								<div style={{ color: "#C0392B", fontSize: "0.82rem" }}>Motivo: {r.motivoCancelamento}</div>
							) : null}
							{r.voucherDisponivel ? (
										<VoucherButton enviar={() => requestReservationVoucher(token, r.bookingId || r.id)} />
									) : null}
						</div>
						<Tag value={st.label} severity={st.severity} />
					</div>
				);
			})}
			{(quote.reservas || []).some((r) => r.status === "pendente") ? (
				<div style={{ marginTop: "1rem", background: "#FFF6E6", borderRadius: 10, padding: "10px 14px", fontSize: "0.85rem", color: "#8A5B00" }}>
					{/* Sem citar "link enviado": o envio depende da agência (owner 2026-08-04). */}
					<i className="pi pi-info-circle" /> A <strong>tarifa e a disponibilidade já estão garantidas</strong> e não mudam mais.
					O <strong>valor em reais</strong>, porém, acompanha o câmbio: ele é recalculado pela cotação de câmbio vigente
					até o pagamento ser concluído — e só então fica congelado. Sua agência informa o valor final e a forma de pagamento.
					{/* Com mais de uma reserva na mesma cotação, o aviso acima soava como se TUDO
					    ainda pudesse mudar de valor, inclusive o que já foi pago (owner 2026-08-06). */}
					{(quote.reservas || []).some((r) => r.status === "paga") ? (
						<div style={{ marginTop: 6 }}>
							Isso vale <strong>apenas para as reservas ainda não pagas</strong>. As que já foram pagas
							estão com o valor <strong>congelado</strong> e não mudam mais.
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
};

const Loading = () => (
	<div style={{ padding: "2.5rem 0", textAlign: "center" }}>
		<ProgressSpinner style={{ width: 44, height: 44 }} strokeWidth="4" />
		<p style={{ color: "#6B6B6B", marginTop: "1rem" }}>Atualizando disponibilidade, preços e câmbio…</p>
	</div>
);

// Padrão das páginas públicas (HotelView/PaymentLink): logo Dream4 no topo + conteúdo
// num card branco alinhado à ESQUERDA.
const Shell = ({ children, nome, contato, cambio }) => {
	const janela = descreverCambio(cambio);
	return (
	<div style={{ minHeight: "100vh", background: "#F2F4F7", padding: "1.5rem", display: "flex", justifyContent: "center" }}>
		<ConfirmDialog />
		<div style={{ width: "min(720px, 100%)" }}>
			<div style={{ marginBottom: "1rem" }}>
				<img src={dream4Logo} alt="Dream4" style={{ height: "34px" }} />
			</div>
			{janela ? (
				<div style={{ background: "#EEE6FB", borderRadius: 12, padding: "10px 14px", marginBottom: "1rem", fontSize: "0.88rem", color: "#4A2E7A", display: "flex", flexDirection: "column", gap: 6, textAlign: "left", boxShadow: "0 2px 14px rgba(0,0,0,0.06)" }}>
					<span>
						<i className="pi pi-dollar" /> {janela.texto}
					</span>
					<span style={{ fontSize: "0.8rem", opacity: 0.85 }}>
						<i className="pi pi-info-circle" /> As tarifas e a disponibilidade são reconsultadas cada vez que você abre esta página e podem variar.
					</span>
				</div>
			) : null}
			<div style={{ background: "#fff", borderRadius: "16px", padding: "1.5rem", boxShadow: "0 2px 14px rgba(0,0,0,0.06)", textAlign: "left" }}>
				{nome ? <div style={{ fontWeight: 700, color: "#2E0249", fontSize: "1.1rem", marginBottom: contato?.nome ? 4 : 12 }}>{nome}</div> : null}
				{contato?.nome ? <p style={{ color: "#6B6B6B", margin: "0 0 12px" }}>Olá, {contato.nome} 👋 — selecione as opções desejadas e aprove sua cotação.</p> : null}
				{children}
			</div>
			<div style={{ textAlign: "center", color: "#9aa0a6", fontSize: "0.75rem", marginTop: "1rem" }}>
				Cotação apresentada pela Dream4.
			</div>
		</div>
	</div>
	);
};

const Estado = ({ titulo, texto, icone, cor }) => (
	<div style={{ padding: "1.5rem 0", textAlign: "center" }}>
		{icone ? <i className={`pi ${icone}`} style={{ fontSize: "2.5rem", color: cor || "#814DE5", marginBottom: "1rem", display: "block" }} /> : null}
		<h2 style={{ color: "#2E0249", marginBottom: "0.5rem" }}>{titulo}</h2>
		<p style={{ color: "#6B6B6B", maxWidth: 460, margin: "0 auto" }}>{texto}</p>
	</div>
);

export default QuoteSelection;
